import { z } from 'zod';
import { cents, entityId, isoDateTime, slug } from './primitives.js';

/**
 * CONTRATO DO SERVICO DE PAGAMENTOS
 *
 * Espelha os DTOs publicos da API em C# (`Solote.Payments.Api`). Este arquivo
 * e a fonte da verdade para o front; qualquer divergencia com o servico
 * aparece no teste de contrato (`npm run test:contracts`).
 *
 * Nenhum dado de cartao passa por aqui. O front tokeniza direto no SDK do
 * provedor e envia apenas o token — a API nunca ve PAN, CVV ou validade
 * (requisito de escopo PCI SAQ-A).
 */

export const paymentMethod = z.enum(['pix', 'credit_card', 'boleto']);
export type PaymentMethod = z.infer<typeof paymentMethod>;

export const paymentStatus = z.enum([
  'pending',
  'authorized',
  'paid',
  'failed',
  'canceled',
  'refunded',
  'partially_refunded',
  'charged_back',
  'expired',
]);
export type PaymentStatus = z.infer<typeof paymentStatus>;

export const subscriptionStatus = z.enum([
  /**
   * Criada, aguardando o primeiro pagamento confirmar. NAO concede os limites
   * do plano — quem gera um Pix e nao paga fica aqui.
   */
  'incomplete',
  'trialing',
  'active',
  'past_due',
  'paused',
  'canceled',
  'expired',
]);
export type SubscriptionStatus = z.infer<typeof subscriptionStatus>;

/** Requisicao de checkout. `idempotencyKey` vai no header Idempotency-Key. */
export const createCheckoutRequest = z.object({
  /** Assinatura de plano ou compra avulsa de produto. */
  kind: z.enum(['subscription', 'one_time']),
  planSlug: slug.nullable().default(null),
  billingPeriod: z.enum(['monthly', 'quarterly', 'semiannual', 'annual', 'one_time']).nullable().default(null),
  productSlug: slug.nullable().default(null),
  quantity: z.number().int().min(1).default(1),
  /** Alvo do produto avulso, ex.: destacar o anuncio X. */
  targetListingId: entityId.nullable().default(null),
  method: paymentMethod,
  couponCode: z.string().nullable().default(null),
  /** Token do cartao gerado no cliente. Obrigatorio em credit_card. */
  cardToken: z.string().nullable().default(null),

  /**
   * BANDEIRA do cartao (visa, master, elo, amex, hipercard), resolvida no
   * cliente a partir do BIN durante a tokenizacao.
   *
   * Vem junto com o token porque so o cliente a conhece: quem ve o numero
   * do cartao e o formulario seguro do provedor, e o servidor nunca ve.
   * Sem ela o Mercado Pago recusa a cobranca com `Invalid
   * payment_method_id` — ele nao deduz a bandeira a partir do token.
   */
  cardBrand: z.string().nullable().default(null),

  /**
   * Impressao do DISPOSITIVO, calculada no navegador pelo `security.js` do
   * provedor.
   *
   * Vai adiante como cabecalho na cobranca e e um dos principais insumos da
   * analise de risco do cartao. Sem ela o provedor nao sabe nada sobre o
   * aparelho e decide pelo lado conservador.
   *
   * Opcional de proposito: bloqueador de anuncio derruba esse script com
   * frequencia, e a falta dele nao pode impedir a cobranca.
   */
  deviceId: z.string().nullable().default(null),
  installments: z.number().int().min(1).max(12).default(1),
  payer: z.object({
    name: z.string().min(2),
    email: z.string().email(),
    /** CPF ou CNPJ, somente digitos. */
    document: z.string().min(11).max(14),
    phone: z.string().nullable().default(null),
    address: z
      .object({
        zipCode: z.string(),
        street: z.string(),
        number: z.string(),
        complement: z.string().nullable().default(null),
        neighborhood: z.string(),
        city: z.string(),
        state: z.string().length(2),
      })
      .nullable()
      .default(null),
  }),
  returnUrl: z.string().nullable().default(null),
});

export type CreateCheckoutRequest = z.infer<typeof createCheckoutRequest>;

/** Dados de exibicao do Pix. `qrCode` e copia-e-cola; `qrCodeBase64` e a imagem. */
export const pixDetails = z.object({
  qrCode: z.string(),
  qrCodeBase64: z.string().nullable().default(null),
  expiresAt: isoDateTime,
});

export const boletoDetails = z.object({
  barcode: z.string(),
  digitableLine: z.string(),
  pdfUrl: z.string(),
  dueDate: isoDateTime,
});

export const cardDetails = z.object({
  brand: z.string().nullable().default(null),
  last4: z.string().length(4).nullable().default(null),
  installments: z.number().int().min(1).default(1),
  /** Preenchido quando o emissor exige autenticacao 3DS. */
  authenticationUrl: z.string().nullable().default(null),
});

export const payment = z.object({
  id: entityId,
  /** Codigo curto exibido ao cliente e usado no suporte. */
  reference: z.string(),
  status: paymentStatus,
  method: paymentMethod,
  amountCents: cents,
  discountCents: cents.default(0),
  currency: z.string().length(3).default('BRL'),
  /** Nome do provedor que processou — util no suporte e na conciliacao. */
  provider: z.string(),
  pix: pixDetails.nullable().default(null),
  boleto: boletoDetails.nullable().default(null),
  card: cardDetails.nullable().default(null),
  /** Motivo legivel quando `failed`. Nunca expor mensagem crua do provedor. */
  failureReason: z.string().nullable().default(null),
  createdAt: isoDateTime,
  paidAt: isoDateTime.nullable().default(null),
  expiresAt: isoDateTime.nullable().default(null),
});

export type Payment = z.infer<typeof payment>;

export const subscription = z.object({
  id: entityId,
  planSlug: slug,
  planName: z.string(),
  status: subscriptionStatus,
  billingPeriod: z.string(),
  amountCents: cents,
  currentPeriodStart: isoDateTime,
  currentPeriodEnd: isoDateTime,
  cancelAtPeriodEnd: z.boolean().default(false),
  trialEndsAt: isoDateTime.nullable().default(null),
  /** Ultima cobranca gerada — o painel do anunciante mostra o status. */
  latestPayment: payment.nullable().default(null),
  createdAt: isoDateTime,
  canceledAt: isoDateTime.nullable().default(null),
});

export type Subscription = z.infer<typeof subscription>;

export const checkoutResponse = z.object({
  payment,
  subscription: subscription.nullable().default(null),
  /** Redirect para checkout hospedado, quando o provedor exigir. */
  redirectUrl: z.string().nullable().default(null),
});

export type CheckoutResponse = z.infer<typeof checkoutResponse>;

/** Erro padronizado: RFC 7807 (ProblemDetails), como a API C# devolve. */
export const problemDetails = z.object({
  type: z.string().default('about:blank'),
  title: z.string(),
  status: z.number().int(),
  detail: z.string().nullable().default(null),
  instance: z.string().nullable().default(null),
  /** Correlaciona com o log estruturado do servidor. */
  traceId: z.string().nullable().default(null),
  errors: z.record(z.string(), z.array(z.string())).nullable().default(null),
});

export type ProblemDetails = z.infer<typeof problemDetails>;
